import * as React from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { CommentReply } from '../../../../ui/tiptap/extensions/comment-mark';

/** One thread's worth of state, collapsed from its `comment` mark(s) in the doc. */
export interface CommentThreadSummary {
  commentId: string;
  resolved: boolean;
  replies: CommentReply[];
  /** The commented text itself, truncated for the rail's quote line. */
  anchorText: string;
  /**
   * Document position of the thread's first character, for anything that has to find
   * the words on screen — the margin chips measure their line from it.
   */
  anchorFrom: number;
}

export interface CommentThreadCounts {
  open: number;
  resolved: number;
}

const ANCHOR_TEXT_MAX_LENGTH = 120;

/** Shared so "no editor yet" does not hand every consumer a fresh array to diff. */
const NO_THREADS: CommentThreadSummary[] = [];

function truncate(text: string): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= ANCHOR_TEXT_MAX_LENGTH) {
    return collapsed;
  }
  return `${collapsed.slice(0, ANCHOR_TEXT_MAX_LENGTH).trimEnd()}…`;
}

/**
 * Collapses every `comment` mark in the document into one summary per thread, in
 * document order of first appearance.
 *
 * Walks the ProseMirror document rather than the saved TipTap JSON. The rail and the
 * header's count both want to be right *while* someone types, and `editor.getJSON()`
 * rebuilds the entire document as plain objects every time it is called.
 *
 * Every mark on a text node is inspected, not just the first: `comment` is declared
 * `excludes: ''` so overlapping threads coexist on the same words, and a thread whose
 * range is entirely inside another one would otherwise never appear in the rail.
 */
export function extractCommentThreads(
  doc: ProseMirrorNode
): CommentThreadSummary[] {
  const order: string[] = [];
  const byId = new Map<
    string,
    { resolved: boolean; replies: CommentReply[]; text: string; from: number }
  >();

  doc.descendants((node, pos) => {
    if (!node.isText) {
      return;
    }
    for (const mark of node.marks) {
      if (mark.type.name !== 'comment') {
        continue;
      }
      const commentId = mark.attrs['commentId'];
      if (typeof commentId !== 'string') {
        continue;
      }
      const existing = byId.get(commentId);
      if (existing === undefined) {
        order.push(commentId);
        byId.set(commentId, {
          resolved: mark.attrs['resolved'] === true,
          replies: (mark.attrs['replies'] as CommentReply[] | undefined) ?? [],
          text: node.text ?? '',
          from: pos,
        });
        continue;
      }
      existing.text += node.text ?? '';
    }
  });

  return order.map((commentId) => {
    const thread = byId.get(commentId);
    // `order` only ever gains an id alongside a `byId` entry for it, set above.
    if (thread === undefined) {
      throw new Error(`Missing comment thread state for ${commentId}`);
    }
    return {
      commentId,
      resolved: thread.resolved,
      replies: thread.replies,
      anchorText: truncate(thread.text),
      anchorFrom: thread.from,
    };
  });
}

/**
 * Cheap stand-in for deep equality, so a keystroke that changes no thread re-renders
 * nothing.
 *
 * Every message's text is in here, not just how many there are. Editing a message
 * changes neither the count nor the ids, so a signature built from those alone
 * reported "nothing has changed" for the one edit that had — the document was rewritten
 * and no surface showing it ever re-read it, which looks exactly like a save that did
 * not happen.
 */
function threadsSignature(threads: CommentThreadSummary[]): string {
  return threads
    .map(
      (thread) =>
        `${thread.commentId}:${thread.resolved ? 1 : 0}:${thread.anchorText}:${
          thread.anchorFrom
        }:${thread.replies
          .map((reply) => `${reply.id}=${reply.body}`)
          .join(',')}`
    )
    .join('|');
}

function countThreads(threads: CommentThreadSummary[]): CommentThreadCounts {
  return threads.reduce<CommentThreadCounts>(
    (acc, thread) => {
      if (thread.resolved) {
        acc.resolved += 1;
      } else {
        acc.open += 1;
      }
      return acc;
    },
    { open: 0, resolved: 0 }
  );
}

export interface UseCommentThreadsResult {
  threads: CommentThreadSummary[];
  counts: CommentThreadCounts;
}

/**
 * The document's comment threads, tracked live off the editor.
 *
 * Subscribing through `useEditorState` rather than the page's document watchable is
 * what keeps the editor's own re-render out of its host page: the selector runs on
 * every transaction, but {@link threadsSignature} stops an ordinary keystroke from
 * re-rendering the rail, and the page itself never subscribes to the prose at all.
 */
export function useCommentThreads(
  editor: Editor | null
): UseCommentThreadsResult {
  const threads = useEditorState({
    editor,
    selector: ({ editor }) =>
      editor === null || editor === undefined
        ? []
        : extractCommentThreads(editor.state.doc),
    // `b` is null on the first comparison, before anything has been selected.
    equalityFn: (a, b) =>
      b !== null && threadsSignature(a) === threadsSignature(b),
  });

  const safeThreads = threads ?? NO_THREADS;

  return React.useMemo(
    () => ({ threads: safeThreads, counts: countThreads(safeThreads) }),
    [safeThreads]
  );
}

/**
 * The thread the caret sits in. The rail expands this card and scrolls it into view,
 * and `CommentActiveHighlight` paints the matching span in the prose, so clicking
 * either side selects the same thread on both.
 */
export function useActiveCommentId(editor: Editor | null): string | null {
  const activeId = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (editor === null || editor === undefined) {
        return null;
      }
      const commentId = editor.getAttributes('comment')['commentId'];
      return typeof commentId === 'string' ? commentId : null;
    },
  });

  return activeId ?? null;
}
